import Vue from 'vue'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { isSubmissionResponse, notifySubmitted } from '@/libs/moderation'

const getErrorText = error => (error.response && error.response.data && (error.response.data.message || error.response.data.error))
  || error.message
  || 'Произошла ошибка'

/**
 * Добавление трека из результата импорта плейлиста (PlaylistTracksTable, событие add-track):
 * при необходимости создаёт артиста, добавляет трек, обрабатывает заявку модерации
 * и точечно обновляет строку. Требует зарегистрированный store-модуль `playlist`.
 *
 * @param {Object} options
 * @param {Function} options.toast — useToast()
 * @param {Object} options.isLoading — ref флага загрузки
 * @param {Object} [options.errorMessage] — ref текста ошибки (если компонент его показывает)
 * @param {Function} [options.reloadPlaylist] — перезагрузка плейлиста целиком,
 *   когда сервер не вернул идентификатор трека (может вернуть промис)
 */
export default function usePlaylistTrackAdder({
  toast,
  isLoading,
  errorMessage = null,
  reloadPlaylist = null,
}) {
  const setErrorMessage = text => {
    if (errorMessage) errorMessage.value = text
  }

  const addTrack = async track => {
    const firstArtist = track.artists && track.artists[0]

    isLoading.value = true
    setErrorMessage('')

    try {
      let artistId = firstArtist && firstArtist.id ? firstArtist.id : null
      let isArtistCreated = false

      if (!artistId) {
        const artistResponse = await store.dispatch('playlist/createArtist', {
          title: firstArtist ? firstArtist.name : null,
        })
        artistId = artistResponse.data.data.id
        isArtistCreated = true
      }

      const addResponse = await store.dispatch('playlist/addTrack', {
        id: artistId,
        queryParams: { title: track.title },
      })

      // Автор: трек ушёл на одобрение — строку не обновляем, плейлист не перезагружаем
      if (isSubmissionResponse(addResponse)) {
        notifySubmitted(toast)
        return
      }

      const newTrackId = addResponse.data.track_id
        || (addResponse.data.data ? addResponse.data.data.id : addResponse.data.id)

      if (newTrackId) {
        // Точечно обновляем строку: повторный импорт всего плейлиста слишком дорог,
        // Spotify не кешируется и на большом плейлисте упирается в лимиты
        if (isArtistCreated && firstArtist) {
          Vue.set(firstArtist, 'id', artistId)
        }
        Vue.set(track, 'id', newTrackId)
      } else if (reloadPlaylist) {
        // Сервер не вернул идентификатор трека — остаётся перезагрузить плейлист целиком
        await reloadPlaylist()
      }
    } catch (error) {
      setErrorMessage(getErrorText(error))
      toast({
        component: ToastificationContent,
        props: {
          title: 'Не удалось добавить трек',
          text: getErrorText(error),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    } finally {
      isLoading.value = false
    }
  }

  return {
    addTrack,
  }
}
